// src/components/document/PageSettingsEditor.tsx
// Form for editing page settings: paper size, margins, and header/footer content.

import { useState, useCallback } from 'react';
import { useI18n } from '@react/hooks';
import { Card, CardHeader, CardTitle, CardContent, Button } from '@react/components';
import { Select } from '@/components/Select';
import { HeaderFooterEditor, type HeaderFooterZones } from './HeaderFooterEditor';
import { DEFAULT_PAGE_SETTINGS, DEFAULT_MARGINS } from '@/features/document/logic';

interface PageSettingsEditorProps {
  settings?: any;
  onSave: (settings: any) => Promise<void>;
}

export function PageSettingsEditor({ settings, onSave }: PageSettingsEditorProps) {
  const { t } = useI18n();
  const [paperSize, setPaperSize] = useState(settings?.paper_size || DEFAULT_PAGE_SETTINGS.paper_size);
  const [orientation, setOrientation] = useState(settings?.orientation || DEFAULT_PAGE_SETTINGS.orientation);
  const [margins, setMargins] = useState(settings?.margins || { ...DEFAULT_MARGINS });
  const [header, setHeader] = useState<HeaderFooterZones>({
    left: settings?.header?.left || '',
    center: settings?.header?.center || '',
    right: settings?.header?.right || '',
  });
  const [footer, setFooter] = useState<HeaderFooterZones>({
    left: settings?.footer?.left || '',
    center: settings?.footer?.center || '{page}',
    right: settings?.footer?.right || '',
  });
  const [firstPageDifferent, setFirstPageDifferent] = useState(settings?.first_page_different || false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await onSave({
        paper_size: paperSize,
        orientation,
        margins,
        header,
        footer,
        first_page_different: firstPageDifferent,
        page_numbering_start: settings?.page_numbering_start || 1,
      });
    } finally {
      setIsSaving(false);
    }
  }, [paperSize, orientation, margins, header, footer, firstPageDifferent, settings, onSave]);

  return (
    <div className="space-y-4">
      {/* Paper & Orientation */}
      <Card>
        <CardHeader>
          <CardTitle>{t('pageSettings.paperLayout')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {t('document.paged.paperSize')}
              </label>
              <Select value={paperSize} onChange={(e) => setPaperSize(e.target.value)}>
                <option value="PAPER_SIZE_A4">A4 (210 x 297 mm)</option>
                <option value="PAPER_SIZE_A3">A3 (297 x 420 mm)</option>
                <option value="PAPER_SIZE_LETTER">Letter (8.5 x 11 in)</option>
                <option value="PAPER_SIZE_LEGAL">Legal (8.5 x 14 in)</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {t('pageSettings.orientation')}
              </label>
              <Select value={orientation} onChange={(e) => setOrientation(e.target.value)}>
                <option value="PAGE_ORIENTATION_PORTRAIT">{t('document.paged.portrait')}</option>
                <option value="PAGE_ORIENTATION_LANDSCAPE">{t('document.paged.landscape')}</option>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Margins */}
      <Card>
        <CardHeader>
          <CardTitle>{t('pageSettings.margins')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {(['top_mm', 'bottom_mm', 'left_mm', 'right_mm'] as const).map((key) => (
              <div key={key}>
                <label className="block text-xs text-muted-foreground mb-1">
                  {t(`pageSettings.margin.${key.replace('_mm', '')}`)}
                </label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={margins[key]}
                    onChange={(e) => setMargins({ ...margins, [key]: parseInt(e.target.value) || 0 })}
                    min={5}
                    max={100}
                    className="w-full px-2 py-1.5 rounded border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                  />
                  <span className="text-xs text-muted-foreground">mm</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Header & Footer */}
      <Card>
        <CardHeader>
          <CardTitle>{t('pageSettings.headerFooter')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <HeaderFooterEditor
            label={t('pageSettings.header')}
            value={header}
            onChange={setHeader}
          />
          <HeaderFooterEditor
            label={t('pageSettings.footer')}
            value={footer}
            onChange={setFooter}
          />
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={firstPageDifferent}
              onChange={(e) => setFirstPageDifferent(e.target.checked)}
              className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
            />
            <span className="text-sm text-foreground">{t('pageSettings.firstPageDifferent')}</span>
          </label>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? t('common.saving') : t('common.save')}
        </Button>
      </div>
    </div>
  );
}
